import React, {useCallback, useMemo, useState} from "react";
import Dialog from "../../base/dialog";
import {Box, DialogActions, DialogContent, DialogTitle, MenuItem} from "@mui/material";
import Button from "../../base/button/button";
import Form, {MuiRFF, Yup} from "../../form";
import Grid2 from "@mui/material/Unstable_Grid2";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import useIsMounted from "../../../hooks/use-is-mounted";
import {useTranslation} from "react-i18next";
import {CustomerAddressTypes} from "../../../../core/constants/enums";
import useCountryProvinceCache from "../../../hooks/use-country-province-cache";
import EnvService from "../../../../core/services/env";

/**
 * Resolves a country object from address codes/ids against the countries cache.
 * @param {object | undefined} address
 * @param {Array} countries
 * @return {object | null}
 */
const resolveCountry = (address, countries) => {
    const countryCode = address?.countryCode
        ?? (typeof address?.country === 'string' ? address.country : address?.country?.code)
        ?? '';
    const countryId = address?.countryId
        ?? (typeof address?.country === 'object' ? address?.country?.id : undefined);
    return countries.find((c) =>
        (countryCode && c.code === countryCode)
        || (countryId != null && c.id === countryId),
    ) ?? (typeof address?.country === 'object' ? address.country : null)
        ?? null;
};

/**
 * Resolves a province object from address codes/ids against the selected country's provinces.
 * @param {object | undefined} address
 * @param {object | null} country
 * @return {object | null}
 */
const resolveProvince = (address, country) => {
    const provinces = country?.provinces ?? [];
    const provinceCode = address?.provinceCode
        ?? (typeof address?.province === 'string' ? address.province : address?.province?.code)
        ?? '';
    const provinceId = address?.provinceId
        ?? (typeof address?.province === 'object' ? address?.province?.id : undefined);
    return provinces.find((p) =>
        (provinceCode && p.code === provinceCode)
        || (provinceId != null && p.id === provinceId),
    ) ?? (typeof address?.province === 'object' && address.province?.id != null ? address.province : null)
        ?? null;
};

/**
 * Vendor address form: line1–4 + city/postal; country/province kept as objects in form state.
 */
const UpsertVendorAddressDialog = ({
                                       open,
                                       close,
                                       address,
                                       editing = false,
                                       submit,
                                   }) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.vendor-account-info.addressDialog'});
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();
    const {countries} = useCountryProvinceCache(undefined, true);

    const schema = useMemo(() => Yup.object().shape({
        typeId: Yup.number().nullable().required(ValidateMessages.required),
        line1: Yup.string().trim().nullable().required(ValidateMessages.required),
        line2: Yup.string().trim().nullable(),
        line3: Yup.string().trim().nullable(),
        line4: Yup.string().trim().nullable(),
        city: Yup.string().trim().nullable().required(ValidateMessages.required),
        postalCode: Yup.string().trim().nullable().required(ValidateMessages.required),
        country: Yup.mixed().nullable().required(ValidateMessages.required),
        province: Yup.mixed().nullable().required(ValidateMessages.required),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => {
        const country = resolveCountry(address, countries);
        const province = resolveProvince(address, country);
        return {
            typeId: address?.type?.id
                ?? (address?._isBilling ? CustomerAddressTypes.billing : CustomerAddressTypes.shipping),
            line1: address?.line1 ?? address?.streetAddress ?? '',
            line2: address?.line2 ?? address?.aptNo ?? '',
            line3: address?.line3 ?? '',
            line4: address?.line4 ?? '',
            city: address?.city ?? '',
            postalCode: address?.postalCode ?? '',
            country,
            province,
        };
    }, [address, countries]);

    /**
     * Submits line fields and country/province objects to the parent callback.
     */
    const onSubmit = useCallback(async (values) => {
        setSubmitting(true);
        const ok = await submit({
            typeId: values.typeId,
            line1: values.line1 ?? '',
            line2: values.line2 ?? '',
            line3: values.line3 ?? '',
            line4: values.line4 ?? '',
            city: values.city ?? '',
            postalCode: values.postalCode ?? '',
            country: values.country,
            province: values.province,
        });
        if (!isMounted())
            return;
        setSubmitting(false);
        if (ok) {
            close();
        }
    }, [close, isMounted, submit]);

    return (
        <Dialog open={open} setOpen={close} maxWidth={'sm'}>
            <DialogTitle>
                {editing ? t('editTitle') : t('addTitle')}
            </DialogTitle>
            <DialogContent dividers>
                <Form
                    key={[
                        initialValues.country?.id,
                        initialValues.province?.id,
                        initialValues.line1,
                        editing,
                        countries.length,
                    ].join('|')}
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{id: 'upsert-vendor-address-form'}}
                >
                    {({values, form}) => {
                        const provinces = values.country?.provinces ?? [];
                        const provinceLabel = !values.country
                            ? t('province')
                            : (values.country.name === EnvService.USATitle ? t('state') : t('province'));

                        return (
                            <Grid2 container spacing={2}>
                                <Grid2 xs={12}>
                                    <MuiRFF.TextField
                                        name={'typeId'}
                                        label={t('type')}
                                        select
                                        fullWidth
                                        required={required.typeId}
                                        disabled={submitting || editing}
                                    >
                                        <MenuItem value={CustomerAddressTypes.shipping}>{t('shipping')}</MenuItem>
                                        <MenuItem value={CustomerAddressTypes.billing}>{t('billing')}</MenuItem>
                                    </MuiRFF.TextField>
                                </Grid2>
                                <Grid2 xs={12}>
                                    <MuiRFF.TextField
                                        name={'line1'}
                                        label={t('line1')}
                                        required={required.line1}
                                        fullWidth
                                        disabled={submitting}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiRFF.TextField
                                        name={'line2'}
                                        label={t('line2')}
                                        fullWidth
                                        disabled={submitting}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiRFF.TextField
                                        name={'line3'}
                                        label={t('line3')}
                                        fullWidth
                                        disabled={submitting}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiRFF.TextField
                                        name={'line4'}
                                        label={t('line4')}
                                        fullWidth
                                        disabled={submitting}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiRFF.TextField
                                        name={'city'}
                                        label={t('city')}
                                        required={required.city}
                                        fullWidth
                                        disabled={submitting}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiRFF.TextField
                                        name={'postalCode'}
                                        label={values.country?.name === EnvService.USATitle ? t('zipCode') : t('postalCode')}
                                        required={required.postalCode}
                                        fullWidth
                                        disabled={submitting}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiRFF.TextField
                                        name={'country'}
                                        label={t('country')}
                                        select
                                        fullWidth
                                        required={required.country}
                                        disabled={submitting || !countries.length}
                                        SelectProps={{
                                            renderValue: (value) => value?.name ?? value?.title ?? '',
                                            onChange: (e) => {
                                                form.change(e.target.name, e.target.value);
                                                form.change('province', null);
                                            },
                                        }}
                                    >
                                        {countries.map((country) => (
                                            <MenuItem key={country.id ?? country.code} value={country}>
                                                {country.name ?? country.title ?? country.code}
                                            </MenuItem>
                                        ))}
                                    </MuiRFF.TextField>
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <MuiRFF.TextField
                                        name={'province'}
                                        label={provinceLabel}
                                        select
                                        fullWidth
                                        required={required.province}
                                        disabled={submitting || !values.country || !provinces.length}
                                        SelectProps={{
                                            renderValue: (value) => value?.name ?? value?.title ?? '',
                                        }}
                                    >
                                        {provinces.map((province) => (
                                            <MenuItem key={province.id ?? province.code} value={province}>
                                                {province.name ?? province.title ?? province.code}
                                            </MenuItem>
                                        ))}
                                    </MuiRFF.TextField>
                                </Grid2>
                            </Grid2>
                        );
                    }}
                </Form>
            </DialogContent>
            <DialogActions className={'p-0'}>
                <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <Button
                        disabled={submitting}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={close}
                        borderRadius={'0 8px 0 8px'}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        disabled={submitting}
                        variant={'dialog'}
                        color={'primary'}
                        type={'submit'}
                        form={'upsert-vendor-address-form'}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {submitting
                            ? (editing ? t('updating') : t('creating'))
                            : (editing ? t('update') : t('create'))}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default UpsertVendorAddressDialog;
